<template>
  <div
    v-if="props.show"
    class="w-full h-full fixed inset-y-0 bg-black/60 z-50 top-0 left-0 flex items-center justify-center"
  >
    <!-- when CS2 servers are disabled -->
    <div
      v-if="cs2ServerDisabled"
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="flex gap-2 items-center">
        <div class="rounded-full h-8 w-8 bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/lock.svg" />
        </div>
        <h1 class="text-text-primary text-xl font-bold flex-1">
          {{ $t('play.start_server.maintenance_title') }}
        </h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>
      <i18n-t scope="global" keypath="play.start_server.maintenance_body" tag="div" class="text-text-secondary text-sm leading-6">
        <template #discord>
          <a
            href="https://discord.gg/refrag"
            target="_blank"
            class="text-primary-500 hover:text-primary-400 underline"
          >Discord</a
          >
        </template>
      </i18n-t>
      <div class="flex flex-col gap-4 text-text-secondary">
        <div class="flex items-center gap-2">
          <img class="w-4 h-4" src="../assets/icons/info-icon.svg" />
          <span>{{ $t('play.start_server.maintenance_working') }}</span>
        </div>
        <div class="flex items-center gap-2">
          <img class="w-4 h-4" src="../assets/icons/discord.svg" />
          <i18n-t scope="global" keypath="play.start_server.maintenance_join_discord" tag="span">
            <template #discord>
              <a
                href="https://discord.gg/refrag"
                target="_blank"
                class="text-primary-500 hover:text-primary-400 underline"
              >Discord</a
              >
            </template>
          </i18n-t>
        </div>
      </div>
    </div>

    <!-- when you are not subscribed -->
    <div
      v-else-if="user.access_level < 1"
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="flex gap-2 items-center">
        <div class="rounded-full h-8 w-8 bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/lock.svg" />
        </div>
        <h1 class="text-text-primary text-xl font-bold flex-1">
          {{ $t('play.start_server.subscribe_title') }}
        </h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>
      <iframe
        class="w-full aspect-[16/9] rounded-sm"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
        src="https://www.youtube.com/embed/QwhgL4SsEfY?si=0qq30030bXs88N5C&autoplay=1&mute=1"
      />
      <div class="text-text-secondary">
        {{ $t('play.start_server.subscribe_body') }}
      </div>
      <ul class="flex flex-col gap-4 text-text-secondary">
        <li class="flex">
          <img class="mr-2" src="../assets/icons/check.svg" />
          <i18n-t scope="global" keypath="play.start_server.subscribe_price" tag="span" class="flex">
            <template #price><span class="text-text-primary px-1">$5.83</span></template>
          </i18n-t>
        </li>
        <li class="flex">
          <img class="mr-2" src="../assets/icons/check.svg" />
          {{ $t('play.start_server.subscribe_on_demand') }}
        </li>
        <li class="flex">
          <img class="mr-2" src="../assets/icons/check.svg" />
          {{ $t('play.start_server.subscribe_community_content') }}
        </li>
      </ul>
      <router-link
        to="upgrade"
        class="bg-primary-500 px-4 py-3 hover:bg-primary-400 cursor-pointer text-text-primary text-semibold flex gap-2 items-center justify-center rounded-md"
      >
        {{ $t('play.start_server.upgrade_now') }}
        <img class="brightness-200" src="../assets/icons/rightarrow.svg" />
      </router-link>
    </div>
    <!-- routine launch on a server this modal did not start: show override options then launch (serverStarted hides it so we show Server is Ready) -->
    <div
      v-else-if="
        isRoutineLaunch
          && activeServer?.id
          && activeServer?.status === 'online'
          && !serverStarted
      "
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="flex gap-2 items-center">
        <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/bigserver.svg" class="h-5 w-5" />
        </div>
        <h1 class="text-text-primary text-2xl font-semibold flex-1">{{ $t('play.start_server.launch_routine') }}</h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>

      <div class="text-text-secondary text-sm leading-6">
        <template v-if="showOverrideOptions">
          {{ $t('play.start_server.routine_ready_overrides') }}
        </template>
        <template v-else>
          {{ $t('play.start_server.routine_ready') }}
        </template>
      </div>

      <template v-if="showOverrideOptions">
        <div class="flex flex-col gap-3">
          <h2 class="text-text-primary text-sm">{{ $t('play.start_server.override_map') }}</h2>
          <Menu as="div" class="relative inline-block text-left w-full">
            <MenuButton
              class="relative w-full inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
            >
              <span class="flex-1 text-left font-light">
                {{ overrideMapId < 0 ? $t('play.start_server.no_override') : (routineMaps.find(m => m.id === overrideMapId)?.friendly_name || routineMaps.find(m => m.id === overrideMapId)?.name) }}
              </span>
              <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
            </MenuButton>
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem v-slot="{ active }" @click="setOverrideMap(-1)">
                  <a href="#" :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']">{{ $t('play.start_server.no_override') }} <CheckIcon v-if="overrideMapId < 0" class="h-5 w-5 ml-auto text-primary-400" /></a>
                </MenuItem>
                <MenuItem v-for="map in routineMaps" :key="map.id" v-slot="{ active }" @click="setOverrideMap(map)">
                  <a href="#" :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']">{{ map.friendly_name || map.name }} <CheckIcon v-if="overrideMapId === map.id" class="h-5 w-5 ml-auto text-primary-400" /></a>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
        <div class="flex flex-col gap-3">
          <h2 class="text-text-primary text-sm">{{ $t('play.start_server.override_preset') }}</h2>
          <Menu as="div" class="relative inline-block text-left w-full">
            <MenuButton
              class="relative w-full inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
            >
              <span class="flex-1 text-left font-light">
                {{ overridePresetId < 0 ? $t('play.start_server.no_override') : (routinePresets.find(p => p.id === overridePresetId)?.friendly_name || routinePresets.find(p => p.id === overridePresetId)?.name) }}
              </span>
              <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
            </MenuButton>
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem v-slot="{ active }" @click="setOverridePreset(-1)">
                  <a href="#" :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']">{{ $t('play.start_server.no_override') }} <CheckIcon v-if="overridePresetId < 0" class="h-5 w-5 ml-auto text-primary-400" /></a>
                </MenuItem>
                <MenuItem v-for="preset in routinePresets" :key="preset.id" v-slot="{ active }" @click="setOverridePreset(preset)">
                  <a href="#" :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']">{{ preset.friendly_name || preset.name }} <CheckIcon v-if="overridePresetId === preset.id" class="h-5 w-5 ml-auto text-primary-400" /></a>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </template>

      <button
        @click="launchTask"
        :disabled="taskLaunchLoading"
        class="bg-primary-600 hover:bg-primary-500 duration-300 cursor-pointer text-text-primary text-semibold flex gap-2 items-center justify-center rounded-md px-4 py-3 disabled:bg-gray-800 disabled:cursor-not-allowed"
      >
        <svg
          v-if="taskLaunchLoading"
          width="16"
          height="16"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="animate-spin"
        >
          <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
          <path d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16" stroke="#4E54C8" stroke-width="2" />
        </svg>
        <img v-else src="../assets/icons/play.svg" class="h-4 w-4 brightness-200" />
        <span>{{ $t('play.start_server.launch_routine') }}</span>
      </button>
    </div>
    <!-- when there is a live server and you want to launch a task with difficulty -->
    <div
      v-else-if="
        activeServer?.id
          && !serverStarted
          && shouldShowDifficultySelector
          && activeServer?.status === 'online'
      "
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="flex gap-2 items-center">
        <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/bigserver.svg" class="h-5 w-5" />
        </div>
        <h1 class="text-text-primary text-2xl font-semibold flex-1">{{ $t('play.start_server.launch_task') }}</h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>

      <div class="text-text-secondary text-sm leading-6">
        {{ $t('play.start_server.task_difficulty_body') }}
      </div>

      <div class="flex flex-col gap-3">
        <h2 class="text-text-primary text-sm">{{ $t('common.difficulty') }}</h2>
        <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
          <MenuButton
            class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
          >
            <h2 class="flex-1 text-left font-light items-center flex gap-2">
              {{ getDifficultyLabel(selectedDifficulty) }}
            </h2>
            <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
          </MenuButton>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem
                  v-for="difficulty in difficulties"
                  :key="difficulty.value"
                  v-slot="{ active }"
                  @click="updateDifficulty(difficulty.value)"
                >
                  <a
                    href="#"
                    :class="[
                      active ? 'text-text-primary' : 'text-text-secondary',
                      'group flex items-center py-2 text-sm w-full justify-start gap-2',
                    ]"
                  >
                    <div class="flex flex-col flex-1">
                      <span>{{ difficulty.label }}</span>
                      <span
                        v-if="difficulty.description"
                        class="text-xs text-text-disabled"
                      >
                        {{ difficulty.description }}
                      </span>
                    </div>
                    <CheckIcon
                      v-if="difficulty.value === selectedDifficulty"
                      class="h-5 w-5 ml-auto text-primary-400 flex-shrink-0"
                    />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>

      <button
        @click="launchTask"
        :disabled="taskLaunchLoading"
        class="bg-primary-600 hover:bg-primary-500 duration-300 cursor-pointer text-text-primary text-semibold flex gap-2 items-center justify-center rounded-md px-4 py-3 disabled:bg-gray-800 disabled:cursor-not-allowed"
      >
        <svg
          v-if="taskLaunchLoading"
          width="16"
          height="16"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="animate-spin"
        >
          <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
          <path
            d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
            stroke="#4E54C8"
            stroke-width="2"
          />
        </svg>
        <span v-else>{{ $t('play.start_server.launch_task') }}</span>
      </button>
    </div>

    <!-- when there is a live server and you want to change the mode. Never a routine: its mode has no map list, and a booting server belongs on the loading panel -->
    <div
      v-else-if="activeServer?.id && !serverStarted && !isRoutineLaunch"
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="flex gap-2 items-center">
        <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/bigserver.svg" class="h-5 w-5" />
        </div>
        <h1 class="text-text-primary text-2xl font-semibold flex-1">
          {{ $t('play.start_server.mode_setup', { mode: capitalizeFirstLetter(props.server.mode) }) }}
        </h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>

      <div class="text-text-secondary text-sm leading-6">
        {{ $t('play.start_server.launch_mode_body', { mode: props.server.mode }) }}
      </div>

      <div class="flex flex-col gap-3">
        <h2 class="text-text-primary text-sm">{{ $t('common.map') }}</h2>
        <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
          <MenuButton
            class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
          >
            <h2 class="flex-1 text-left font-light items-center flex gap-2">
              {{ selectedMap?.friendly_name }}
            </h2>
            <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
          </MenuButton>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem
                  :key="map"
                  v-for="map in maps"
                  v-slot="{ active }"
                  @click="updateMap(map)"
                >
                  <a
                    href="#"
                    :class="[
                      active ? 'text-text-primary' : 'text-text-secondary',
                      'group flex items-center py-2 text-sm w-full justify-start gap-2',
                    ]"
                  >
                    {{ map.friendly_name }}
                    <CheckIcon
                      v-if="map.name === selectedMap?.name"
                      class="h-5 w-5 ml-auto text-primary-400"
                    />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>

      <button
        @click="launchMode"
        class="bg-primary-600 hover:bg-primary-500 duration-300 cursor-pointer text-text-primary text-semibold flex gap-2 items-center justify-center rounded-md px-4 py-3"
      >
        {{ $t('play.start_server.launch_mode', { mode: capitalizeFirstLetter(props.server.mode) }) }}
        <img
          v-if="!modLoading"
          src="../assets/icons/play.svg"
          class="h-4 w-4 brightness-200"
        />
        <div
          v-else
          class="inline-flex h-8 w-8 items-centerborder border-transparent rounded-md text-sm font-medium text-white focus:outline-none focus:ring-0 focus:ring-offset-0 flex justify-center items-center"
        >
          <svg
            class="animate-spin h-full font-normal w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </button>
    </div>
    <!-- when you try and launch a server and you have the prop showMapSelector -->
    <div
      v-else-if="props.showMapSelector && !activeServer?.id"
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="flex gap-2 items-center">
        <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/bigserver.svg" class="h-5 w-5" />
        </div>
        <h1 class="text-text-primary text-2xl font-semibold flex-1">
          {{ $t('play.start_server.mode_setup', { mode: capitalizeFirstLetter(props.server.mode) }) }}
        </h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>

      <div class="text-text-secondary text-sm leading-6">
        {{ $t('play.start_server.start_mode_body', { mode: props.server.mode }) }}
      </div>

      <div class="flex flex-col gap-3">
        <h2 class="text-text-primary text-sm">{{ $t('play.start_server.location') }}</h2>
        <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
          <MenuButton
            class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
          >
            <div class="flex-1 text-left font-light items-center flex gap-2">
              <flag
                :rounded="true"
                :iso="selectedLocation?.flag || storedLocationParsed?.flag"
                class="rounded-full h-4 w-4"
              />
              {{ selectedLocation?.name || storedLocationParsed?.name }}
              <span class="text-text-disabled">•</span>
              {{ latencyLabel(selectedLocation || storedLocationParsed) }}
            </div>
            <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
          </MenuButton>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem
                  :key="server"
                  v-for="server in servers"
                  v-slot="{ active }"
                  @click="updateSelectedLocation(server)"
                >
                  <a
                    href="#"
                    :class="[
                      active ? 'text-text-primary' : 'text-text-secondary',
                      'group flex items-center py-2 text-sm w-full justify-start gap-2',
                    ]"
                  >
                    <flag
                      :rounded="true"
                      :iso="server.flag"
                      class="rounded-full h-4 w-4"
                    />
                    {{ server?.name }} <span class="text-text-disabled">•</span>
                    {{ latencyLabel(server) }}
                    <CheckIcon
                      v-if="server?.name === selectedLocation?.name"
                      class="h-5 w-5 ml-auto text-primary-400"
                    />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>

      <div class="flex flex-col gap-3">
        <h2 class="text-text-primary text-sm">{{ $t('common.map') }}</h2>
        <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
          <MenuButton
            class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
          >
            <h2 class="flex-1 text-left font-light items-center flex gap-2">
              {{ selectedMap?.friendly_name }}
            </h2>
            <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
          </MenuButton>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem
                  :key="map"
                  v-for="map in maps"
                  v-slot="{ active }"
                  @click="updateMap(map)"
                >
                  <a
                    href="#"
                    :class="[
                      active ? 'text-text-primary' : 'text-text-secondary',
                      'group flex items-center py-2 text-sm w-full justify-start gap-2',
                    ]"
                  >
                    {{ map.friendly_name }}
                    <CheckIcon
                      v-if="map.name === selectedMap?.name"
                      class="h-5 w-5 ml-auto text-primary-400"
                    />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>

      <button
        @click="startServer"
        class="bg-primary-600 hover:bg-primary-500 duration-300 cursor-pointer text-text-primary text-semibold flex gap-2 items-center justify-center rounded-md px-4 py-3"
      >
        {{ $t('play.shared.start_server') }}
        <img src="../assets/icons/play.svg" class="h-4 w-4 brightness-200" />
      </button>
    </div>

    <!-- when you launch a server and have no active server id and this is the default -->
    <div
      v-else-if="!activeServer?.id && !startingServer"
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col gap-6"
    >
      <div class="flex gap-2 items-center">
        <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/bigserver.svg" class="h-5 w-5" />
        </div>
        <h1 class="text-text-primary text-2xl font-semibold flex-1">{{ $t('play.start_server.server_setup') }}</h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>

      <div class="flex flex-col gap-3">
        <h2 class="text-text-primary text-sm">{{ $t('play.start_server.location') }}</h2>
        <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
          <MenuButton
            class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
          >
            <div class="flex-1 text-left font-light items-center flex gap-2">
              <flag
                :rounded="true"
                :iso="selectedLocation?.flag"
                class="rounded-full h-4 w-4"
              />
              {{ selectedLocation?.name }}
              <span class="text-text-disabled">•</span>
              <span>{{ latencyLabel(selectedLocation) }}</span>
            </div>
            <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
          </MenuButton>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem
                  :key="server"
                  v-for="server in servers"
                  v-slot="{ active }"
                  @click="updateSelectedLocation(server)"
                >
                  <a
                    href="#"
                    :class="[
                      active ? 'text-text-primary' : 'text-text-secondary',
                      'group flex items-center py-2 text-sm w-full justify-start gap-2',
                    ]"
                  >
                    <flag
                      :rounded="true"
                      :iso="server.flag"
                      class="rounded-full h-4 w-4"
                    />
                    {{ server?.name }}
                    <span class="text-text-disabled">•</span>
                    <span>{{ latencyLabel(server) }}</span>
                    <CheckIcon
                      v-if="server?.name === selectedLocation?.name"
                      class="h-5 w-5 ml-auto text-primary-400"
                    />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
      <div v-if="shouldShowDifficultySelector" class="flex flex-col gap-3">
        <h2 class="text-text-primary text-sm">{{ $t('common.difficulty') }}</h2>
        <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
          <MenuButton
            class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
          >
            <h2 class="flex-1 text-left font-light items-center flex gap-2">
              {{ getDifficultyLabel(selectedDifficulty) }}
            </h2>
            <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
          </MenuButton>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem
                  v-for="difficulty in difficulties"
                  :key="difficulty.value"
                  v-slot="{ active }"
                  @click="updateDifficulty(difficulty.value)"
                >
                  <a
                    href="#"
                    :class="[
                      active ? 'text-text-primary' : 'text-text-secondary',
                      'group flex items-center py-2 text-sm w-full justify-start gap-2',
                    ]"
                  >
                    <div class="flex flex-col flex-1">
                      <span>{{ difficulty.label }}</span>
                      <span
                        v-if="difficulty.description"
                        class="text-xs text-text-disabled"
                      >
                        {{ difficulty.description }}
                      </span>
                    </div>
                    <CheckIcon
                      v-if="difficulty.value === selectedDifficulty"
                      class="h-5 w-5 ml-auto text-primary-400 flex-shrink-0"
                    />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
      <template v-if="isRoutineLaunch && showOverrideOptions">
        <div class="flex flex-col gap-3">
          <h2 class="text-text-primary text-sm">{{ $t('play.start_server.override_map') }}</h2>
          <Menu as="div" class="relative inline-block text-left w-full">
            <MenuButton
              class="relative w-full inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
            >
              <span class="flex-1 text-left font-light">
                {{ overrideMapId < 0 ? $t('play.start_server.no_override') : (routineMaps.find(m => m.id === overrideMapId)?.friendly_name || routineMaps.find(m => m.id === overrideMapId)?.name) }}
              </span>
              <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
            </MenuButton>
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem v-slot="{ active }" @click="setOverrideMap(-1)">
                  <a
                    href="#"
                    :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']"
                  >
                    {{ $t('play.start_server.no_override') }}
                    <CheckIcon v-if="overrideMapId < 0" class="h-5 w-5 ml-auto text-primary-400" />
                  </a>
                </MenuItem>
                <MenuItem
                  v-for="map in routineMaps"
                  :key="map.id"
                  v-slot="{ active }"
                  @click="setOverrideMap(map)"
                >
                  <a
                    href="#"
                    :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']"
                  >
                    {{ map.friendly_name || map.name }}
                    <CheckIcon v-if="overrideMapId === map.id" class="h-5 w-5 ml-auto text-primary-400" />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
        <div class="flex flex-col gap-3">
          <h2 class="text-text-primary text-sm">{{ $t('play.start_server.override_preset') }}</h2>
          <Menu as="div" class="relative inline-block text-left w-full">
            <MenuButton
              class="relative w-full inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
            >
              <span class="flex-1 text-left font-light">
                {{ overridePresetId < 0 ? $t('play.start_server.no_override') : (routinePresets.find(p => p.id === overridePresetId)?.friendly_name || routinePresets.find(p => p.id === overridePresetId)?.name) }}
              </span>
              <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
            </MenuButton>
            <MenuItems
              class="absolute right-0 z-50 mt-2 w-full max-h-48 overflow-y-auto origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1 flex flex-col px-4">
                <MenuItem v-slot="{ active }" @click="setOverridePreset(-1)">
                  <a
                    href="#"
                    :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']"
                  >
                    {{ $t('play.start_server.no_override') }}
                    <CheckIcon v-if="overridePresetId < 0" class="h-5 w-5 ml-auto text-primary-400" />
                  </a>
                </MenuItem>
                <MenuItem
                  v-for="preset in routinePresets"
                  :key="preset.id"
                  v-slot="{ active }"
                  @click="setOverridePreset(preset)"
                >
                  <a
                    href="#"
                    :class="[active ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start gap-2']"
                  >
                    {{ preset.friendly_name || preset.name }}
                    <CheckIcon v-if="overridePresetId === preset.id" class="h-5 w-5 ml-auto text-primary-400" />
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </template>
      <div class="text-text-secondary text-sm leading-6">
        {{ $t('play.start_server.start_set_body') }}
        <br /><br />

        {{ $t('play.start_server.start_set_after', { button: $t('play.shared.start_server') }) }}
      </div>
      <button
        @click="startServer"
        class="bg-primary-600 hover:bg-primary-500 duration-300 cursor-pointer text-text-primary text-semibold flex gap-2 items-center justify-center rounded-md px-4 py-3"
      >
        {{ $t('play.shared.start_server') }}
        <img src="../assets/icons/play.svg" class="h-4 w-4 brightness-200" />
      </button>
    </div>
    <!-- when the server is loading -->
    <div
      v-else-if="activeServer?.status !== 'online'"
      class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col"
    >
      <div class="flex gap-3 pb-6 items-center">
        <!-- spinner -->
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="animate-spin"
        >
          <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
          <path
            d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
            stroke="#4E54C8"
            stroke-width="2"
          />
        </svg>
        <h1 class="text-text-primary text-2xl font-semibold flex-1">{{ $t('play.start_server.loading_server') }}</h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>
      <div class="w-full pt-6 border-t border-gray-800 flex-col gap-6">
        <h1 class="text-text-primary font-medium pb-6">{{ $t('common.details') }}</h1>
        <div class="grid grid-cols-2 gap-6">
          <div class="text-text-secondary">{{ $t('play.start_server.location') }}</div>
          <div class="flex gap-2 justify-end items-center">
            <flag
              v-if="location?.flag"
              :rounded="true"
              :iso="location?.flag"
              class="rounded-full h-4 w-4"
            />
            <div class="text-text-primary text-right">{{ location?.name }}</div>
            <div v-if="location?.ms" class="flex gap-2 items-center">
              <span class="text-text-secondary text-sm">•</span>
              <div class="text-text-primary text-right">{{ location?.ms }}ms</div>
            </div>
          </div>
          <div v-if="props.server.cs_map" class="text-text-secondary">{{ $t('common.map') }}</div>
          <div v-if="props.server.cs_map" class="flex gap-2 justify-end items-center">
            <img
              :src="`/maps/map_icon_${props.server?.cs_map?.name}.svg`"
              class="h-4 w-4"
            />
            <div class="text-text-primary text-right">
              {{ props.server?.cs_map?.friendly_name }}
            </div>
          </div>
          <div class="text-text-secondary">{{ $t('play.start_server.mode') }}</div>
          <div class="text-text-primary text-right">{{ props.server?.mode }}</div>
          <div v-if="props.server.content?.id" class="text-text-secondary">{{ $t('play.start_server.set') }}</div>
          <div v-else class="text-text-secondary">{{ $t('play.start_server.content') }}</div>
          <div class="text-text-primary text-right">
            {{ props.server?.content?.title || props.server?.content?.name }}
          </div>
        </div>
      </div>
    </div>
    <!-- when the server is ready -->
    <div v-else class="w-[500px] bg-gray-900 rounded-lg p-6 flex flex-col">
      <div class="flex gap-3 pb-6 items-center">
        <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
          <img src="../assets/icons/bigserver.svg" class="h-5 w-5" />
        </div>
        <h1 class="text-text-primary text-2xl font-semibold flex-1">{{ $t('play.start_server.server_ready') }}</h1>
        <XMarkIcon
          @click="closeModal"
          class="w-6 h-6 text-text-secondary hover:scale-105 duration-300 hover:text-text-primary cursor-pointer"
        />
      </div>
      <div class="w-full pt-6 border-t border-gray-800 flex-col gap-6">
        <h1 class="text-text-primary font-medium pb-6">{{ $t('common.details') }}</h1>
        <div class="grid grid-cols-2 gap-6 pb-6">
          <div class="text-text-secondary">{{ $t('play.start_server.location') }}</div>
          <div class="flex gap-2 justify-end items-center">
            <flag
              v-if="location?.flag"
              :rounded="true"
              :iso="location?.flag"
              class="rounded-full h-4 w-4"
            />
            <div class="text-text-primary text-right">{{ location?.name }}</div>
            <div v-if="location?.ms" class="flex gap-2 items-center">
              <span class="text-text-secondary text-sm">•</span>
              <div class="text-text-primary text-right">{{ location?.ms }}ms</div>
            </div>
          </div>
          <div class="text-text-secondary">{{ $t('common.map') }}</div>
          <div class="flex gap-2 justify-end items-center">
            <img
              :src="`/maps/map_icon_${props.server?.cs_map?.name}.svg`"
              class="h-4 w-4"
            />
            <div class="text-text-primary text-right">
              {{ props.server?.cs_map?.friendly_name }}
            </div>
          </div>
          <div class="text-text-secondary">{{ $t('play.start_server.mode') }}</div>
          <div class="text-text-primary text-right">{{ props.server?.mode }}</div>
          <div class="text-text-secondary">{{ $t('play.start_server.set') }}</div>
          <div class="text-text-primary text-right">
            {{ props.server?.content?.title || props.server?.content?.name }}
          </div>
        </div>

        <div class="w-full py-6 border-y border-gray-800">
          <div
            class="w-full bg-gray-800 px-2 py-1.5 rounded-lg flex gap-2 border border-gray-700 items-center cursor-pointer duration-300"
            :class="copySucceededVal ? 'border-green-500' : 'border-gray-700'"
            v-clipboard:copy="
              `connect ${activeServer.ip}:${activeServer.port}; password ${activeServer.password}`
            "
            v-clipboard:success="copySucceeded"
          >
            <h1 class="text-text-secondary font-light text-sm flex-1">
              connect {{ activeServer.ip }}:{{ activeServer.port }}; password
              {{ activeServer.password }}
            </h1>
            <img src="../assets/icons/copy.svg" class="h-4 w-4 brightness-75" />
          </div>
        </div>

        <a
          :href="`steam://connect/${activeServer.raw_ip}:${activeServer.port}/${activeServer.password}`"
          class="mt-6 flex gap-2 bg-primary-600 hover:bg-primary-500 duration-300 cursor-pointer w-full py-3 rounded-lg items-center justify-center"
        >
          <img src="../assets/icons/play.svg" class="h-4 w-4 brightness-200" />
          <h1 class="text-text-primary">{{ $t('play.shared.join') }}</h1>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue';
import {
  Menu, MenuButton, MenuItem, MenuItems,
} from '@headlessui/vue';
import { XMarkIcon, CheckIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import useStartServer from '../composables/useStartServer.js';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  server: {
    type: Object,
    required: true,
  },
  showMapSelector: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggleModal']);
const {
  user, cs2ServerDisabled, closeModal, isRoutineLaunch, activeServer,
  serverStarted, showOverrideOptions, overrideMapId, routineMaps, setOverrideMap,
  overridePresetId, routinePresets, setOverridePreset, launchTask, taskLaunchLoading,
  shouldShowDifficultySelector, getDifficultyLabel, selectedDifficulty, difficulties, updateDifficulty,
  capitalizeFirstLetter, selectedMap, maps, updateMap, launchMode,
  modLoading, selectedLocation, storedLocationParsed, latencyLabel, servers,
  updateSelectedLocation, startServer, startingServer, location, copySucceededVal,
  copySucceeded,
} = useStartServer(props, emit);

function handleKeyDown(event) {
  if (event.key === 'Escape' && props.show) {
    closeModal();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>
